import { useEffect, useMemo, useRef, useState } from 'react'
import { Select } from '@forwardflow/ui-v2'
import { useAppStore } from '@forwardflow/store/appStore'
import { IStage, useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import { initialStageMappingsGroups } from '@forwardflow/constants/onboarding.constants'
import {
   useGetFubStages,
   useGetPeopleTags,
   useGetSuggestedStageMappings,
} from '@forwardflow/queries/onboarding.queries'
import {
   DEMO_STAGES,
   DEMO_SUGGESTIONS,
   DEMO_TAGS,
   isFreeFlow,
   isSandbox,
   useDevTools,
} from '../dev/devTools'
import { Card, Chip, Section, Skeleton, StepFooter, StepHeader } from '../shell/Card'
import { listSentence } from '../shell/format'
import {
   addItem,
   applyAllSuggestions,
   hasAnySelection,
   itemKey,
   itemsIn,
   removeItem,
   suggestionsFor,
   TMappedItem,
} from './stage-mapping-explained'
import { TBucketKey } from './stage-mapping-explained/buckets'

/**
 * The three questions, each answered with stages and tags from Follow Up
 * Boss. Every answer stops Forward Flow; they differ only in why, which is
 * what the customer understands. Each maps to the stop buckets the backend
 * already knows, so Settings → Stage Mapping stays in sync.
 */
interface IQuestion {
   id: string
   question: string
   hint: string
   /** Buckets whose items and suggestions this question shows. */
   buckets: TBucketKey[]
   /** Where a manual pick goes. */
   target: TBucketKey
}

const QUESTIONS: IQuestion[] = [
   {
      id: 'working',
      question: 'When you are already working a lead yourself, how will we know?',
      hint: 'The stages or tags that mean "I\'m on it". We never call these leads; they are yours.',
      buckets: ['ff-active-client', 'ff-under-contract'],
      target: 'ff-active-client',
   },
   {
      id: 'finished',
      question: 'Which stages or tags mean a lead is already done?',
      hint: 'Closed, sold, lost, or working with another agent. We leave these alone too.',
      buckets: ['ff-closed-client', 'ff-has-agent'],
      target: 'ff-closed-client',
   },
   {
      id: 'never',
      question: 'Your do-not-call list, and anything else we must never touch',
      hint: 'Do not call, trash, opted out. Add any other stage or tag that should stop us.',
      buckets: ['ff-do-not-contact', 'ff-other-stop'],
      target: 'ff-other-stop',
   },
]

/**
 * Step 6: which leads to leave alone.
 *
 * Forward Flow calls every lead in Follow Up Boss except the ones these
 * three lists point out. Suggestions from the account are pre-applied; the
 * customer confirms, removes, or searches to add. The nurturing and
 * appointment buckets are filled from the suggestions too, silently.
 */
export const StopRules = () => {
   const { currentOrganization } = useAppStore()
   const { onboardingData, getNextStep, getPreviousStep, setStageMappings } =
      useOnboardingStoreV2()
   const orgId = isSandbox() ? '' : onboardingData.organizationId || currentOrganization || ''
   const freeFlow = isFreeFlow()

   const { data: fubStages, isLoading: isLoadingStages } = useGetFubStages(orgId, 10000, 1, true)
   const { data: fubTags, isLoading: isLoadingTags } = useGetPeopleTags(orgId, 1, 10000, true)

   const stages: IStage[] = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ id: String(s.stageId), name: s.stageName, description: '', type: 'stage' as const })) ||
         [],
      [fubStages]
   )
   const tags: IStage[] = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t) => ({ id: t.tagName, name: t.tagName, description: '', type: 'tag' as const })) || [],
      [fubTags]
   )
   const stagesForSuggestion = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ stageId: s.stageId, stageName: s.stageName })) || [],
      [fubStages]
   )
   const tagsForSuggestion = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t, index) => ({ tagId: index + 1, tagName: t.tagName })) || [],
      [fubTags]
   )
   const { data: suggested, isLoading: isLoadingSuggestions } = useGetSuggestedStageMappings(
      stagesForSuggestion,
      tagsForSuggestion,
      !isLoadingStages && !isLoadingTags && !isSandbox()
   )

   const groups: IStageMapGroup[] = onboardingData.stageMappings?.length
      ? onboardingData.stageMappings
      : initialStageMappingsGroups

   const useDemo = freeFlow && !isLoadingStages && !isLoadingTags && stages.length === 0
   const stagesShown = useDemo ? DEMO_STAGES : stages
   const tagsShown = useDemo ? DEMO_TAGS : tags
   const suggestionsShown = useDemo ? DEMO_SUGGESTIONS : suggested?.data
   const isLoadingOptions = !useDemo && (isLoadingStages || isLoadingTags)
   const isWaitingForSuggestions =
      !useDemo &&
      (isLoadingSuggestions ||
         (!suggested &&
            !isLoadingStages &&
            !isLoadingTags &&
            (stagesForSuggestion.length > 0 || tagsForSuggestion.length > 0)))

   // Pre-fill the suggestions once, only on a mapping nobody has touched.
   const prefilled = useRef(false)
   useEffect(() => {
      if (prefilled.current || !suggestionsShown?.length) return
      prefilled.current = true
      if (hasAnySelection(groups)) return
      setStageMappings(applyAllSuggestions(groups, suggestionsShown))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [suggestionsShown])

   const [pickCount, setPickCount] = useState(0)
   const allChosen = useMemo(
      () =>
         new Set(
            QUESTIONS.flatMap((q) => q.buckets).flatMap((b) => itemsIn(groups, b).map(itemKey))
         ),
      [groups]
   )
   const options = useMemo(
      () =>
         [
            {
               group: 'Stages',
               items: stagesShown
                  .filter((s) => !allChosen.has(itemKey({ id: s.id, type: 'stage' })))
                  .map((s) => ({ value: `stage:${s.id}`, label: s.name })),
            },
            {
               group: 'Tags',
               items: tagsShown
                  .filter((t) => !allChosen.has(itemKey({ id: t.id, type: 'tag' })))
                  .map((t) => ({ value: `tag:${t.id}`, label: t.name })),
            },
         ].filter((g) => g.items.length > 0),
      [stagesShown, tagsShown, allChosen]
   )

   const add = (item: TMappedItem, bucket: TBucketKey) => {
      setStageMappings(addItem(groups, item, bucket))
      setPickCount((n) => n + 1)
   }
   const pickFromSearch = (value: string | null, bucket: TBucketKey) => {
      if (!value) return
      const [type, ...rest] = value.split(':')
      const id = rest.join(':')
      const source = type === 'tag' ? tagsShown : stagesShown
      const found = source.find((s) => String(s.id) === id)
      if (found) add({ ...found, type: type as 'stage' | 'tag' }, bucket)
   }

   const totalStops = allChosen.size
   const booked = itemsIn(groups, 'ff-appointment-set')
   const canContinue = (freeFlow || totalStops > 0) && !isLoadingOptions

   useDevTools({
      fill: () => {
         if (suggestionsShown) setStageMappings(applyAllSuggestions(groups, suggestionsShown))
      },
      skip: () => {
         getNextStep()
         return true
      },
   })

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="Which leads should we leave alone?"
               lede="We call every lead in Follow Up Boss except the ones you point out here. Three short lists, already filled in from your stages and tags. Check them, add or remove, done."
            />
            {isLoadingOptions && (
               <div className="space-y-2">
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
               </div>
            )}
         </Card>

         {!isLoadingOptions &&
            QUESTIONS.map((q, qi) => {
               const chosen = q.buckets.flatMap((b) =>
                  itemsIn(groups, b).map((item) => ({ ...item, bucket: b }))
               )
               const suggestions = q.buckets.flatMap((b) =>
                  suggestionsFor(suggestionsShown, groups, b).map((item) => ({ ...item, bucket: b }))
               )
               return (
                  <Card key={q.id} delay={0.06 * (qi + 1)} testId={`stop-${q.id}`}>
                     <Section label={<span className="text-white text-[15px] font-semibold">{q.question}</span>} hint={q.hint}>
                        {chosen.length ? (
                           <div className="flex flex-wrap gap-1.5" data-testid={`stop-${q.id}-chosen`}>
                              {chosen.map((item) => (
                                 <Chip
                                    key={itemKey(item)}
                                    kind={item.type}
                                    onRemove={() => setStageMappings(removeItem(groups, item, item.bucket))}
                                 >
                                    {item.name}
                                 </Chip>
                              ))}
                           </div>
                        ) : (
                           <p className="text-white/40 text-sm">Nothing yet. Add a stage or tag below.</p>
                        )}
                        {isWaitingForSuggestions ? (
                           <div className="flex items-center gap-2">
                              <span className="text-white/45 text-xs">Suggested</span>
                              <Skeleton className="h-7 w-20" />
                           </div>
                        ) : suggestions.length > 0 ? (
                           <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-white/45 text-xs mr-1">Suggested</span>
                              {suggestions.map((item) => (
                                 <Chip key={itemKey(item)} kind={item.type} onAdd={() => add(item, item.bucket)}>
                                    {item.name}
                                 </Chip>
                              ))}
                           </div>
                        ) : null}
                        <Select
                           key={`${q.id}-${pickCount}`}
                           searchable
                           value={null}
                           size="sm"
                           placeholder="Search your Follow Up Boss stages and tags to add"
                           data={options}
                           onChange={(v) => pickFromSearch(v, q.target)}
                           nothingFoundMessage="Nothing in Follow Up Boss matches"
                           comboboxProps={{ withinPortal: true }}
                           data-testid={`stop-${q.id}-search`}
                        />
                     </Section>
                  </Card>
               )
            })}

         {!isLoadingOptions && (
            <Card delay={0.25} className="!py-3.5">
               <p className="text-[#99A1AF] text-xs">
                  Everything else gets called. When a lead books an appointment we move them to{' '}
                  <span className="text-white/80">
                     {booked.length ? listSentence(booked.map((b) => b.name)) : 'your appointment stage'}
                  </span>{' '}
                  in Follow Up Boss and hand them to you. Change any of this later under Settings →
                  Stage Mapping.
               </p>
            </Card>
         )}

         <StepFooter
            onBack={() => getPreviousStep()}
            action={
               isLoadingOptions
                  ? 'Reading your stages'
                  : !canContinue
                    ? 'Add at least one stage or tag'
                    : `Looks right · ${totalStops} ${totalStops === 1 ? 'rule' : 'rules'}`
            }
            onAction={() => {
               setStageMappings(groups)
               getNextStep()
            }}
            disabled={!canContinue}
            note="Last step: your business details, so texting can be approved."
            testId="confirm-stops"
         />
      </div>
   )
}
